/**
 * STEP_GUIDES
 * Content for the first-use instruction bottom sheet.
 * Keyed by protocol step id.
 *
 * Each guide has:
 *   title     — step name
 *   sanskrit  — Sanskrit name
 *   lines     — 3-5 short instruction lines
 *   tip       — single practical tip shown at bottom
 */

export const STEP_GUIDES = {
  kapalbhati: {
    title: "Skull-Shining Breath",
    sanskrit: "Kapalbhati",
    lines: [
      "Sit upright. Spine long, shoulders back.",
      "Exhale sharply through the nose. Your belly pumps inward on each exhale.",
      "Inhales are completely passive — let the lungs refill on their own.",
      "Find a steady rhythm: roughly one exhale per second.",
    ],
    tip: "If you feel dizzy, slow the pace. This is not hyperventilation — it is controlled rapid exhale.",
  },
  nadi_shodhana: {
    title: "Alternate Nostril",
    sanskrit: "Nadi Shodhana",
    lines: [
      "Bring your right hand to your face. Thumb on right nostril, ring finger on left.",
      "Close the right nostril. Inhale through the left for 4 counts.",
      "Close the left. Open the right. Exhale right for 4 counts.",
      "Inhale right 4 counts. Switch. Exhale left 4 counts. That is one cycle.",
    ],
    tip: "The app indicator shows which nostril is active. Follow it — don't count independently.",
  },
  bhramari: {
    title: "Humming Breath",
    sanskrit: "Bhramari",
    lines: [
      "Close your eyes. Gently plug both ears with your index fingers.",
      "Inhale deeply through the nose.",
      "Exhale with your mouth closed, tongue pressed to the roof of your mouth.",
      "As you exhale: produce a low hum from the throat. Feel it resonate in your skull.",
    ],
    tip: "The hum comes from the throat — not the lips. Deeper in the chest vibrates the vagus nerve.",
  },
  rechaka: {
    title: "Extended Exhale",
    sanskrit: "Rechaka",
    lines: [
      "Inhale through the nose for 4 counts.",
      "Exhale slowly through the mouth for 8 counts — twice as long.",
      "Empty the lungs completely before the next inhale.",
      "The extended exhale directly activates your parasympathetic brake.",
    ],
    tip: "Purse your lips slightly on the exhale to slow the breath naturally.",
  },
  trataka: {
    title: "Focused Gazing",
    sanskrit: "Trataka",
    lines: [
      "Place a lit candle at eye level, 2-3 feet in front of you.",
      "Gaze steadily at the tip of the flame. Not staring — soft, relaxed gaze.",
      "Blink naturally. Do not strain or hold your eyes open.",
      "When the mind wanders, gently return to the flame without judgment.",
    ],
    tip: "The candle on screen is a guide — if you have a real candle, use that. The practice is more potent.",
  },
  yoga_nidra: {
    title: "Deep Rest Protocol",
    sanskrit: "Yoga Nidra",
    lines: [
      "Lie down on your back. Arms at your sides, palms open and facing up.",
      "Close your eyes and become completely still.",
      "You will be guided through a full body scan. Simply observe each sensation.",
      "A bell will sound clearly when the practice ends. No need to watch the timer.",
    ],
    tip: "This is not sleep — it is conscious rest. The goal is to stay at the threshold between waking and sleeping.",
  },
  // ── Sleep Descent steps ───────────────────────────────────────────────
  journal_speak: {
    title: "Mind Offload",
    sanskrit: "Manasa Mukti",
    lines: [
      "Close your eyes. Take one slow breath.",
      "Speak freely — tasks, worries, anything circling your mind.",
      "Don't filter or organise. Just let it out.",
      "Tap the mic button again when you feel empty.",
    ],
    tip: "Research shows that naming a task with a specific plan closes the cognitive loop — your brain stops holding it in working memory.",
  },
  journal_review: {
    title: "Review and Plan",
    sanskrit: "Sankalpa",
    lines: [
      "Review what was captured. Edit any item that needs clarifying.",
      "Assign a date to each task — even a rough one.",
      "The date doesn't need to be precise. Tomorrow, This week, Next week all work.",
      "Optional: sync to your calendar or reminders with one tap.",
    ],
    tip: "The date assignment is the key step. A task with a plan is neurologically treated as resolved. Without a date, the brain keeps the loop open.",
  },
  chandra_bhedana: {
    title: "Left Nostril Breath",
    sanskrit: "Chandra Bhedana",
    lines: [
      "Lie down or sit comfortably. Close your right nostril with your right thumb.",
      "Breathe only through the left nostril for this entire practice.",
      "Inhale slowly for 4 counts. Exhale slowly for 4 counts.",
      "Keep the right nostril gently blocked throughout.",
    ],
    tip: "Left nostril breathing activates the right hemisphere and the parasympathetic branch — the body's rest-and-digest system. This is the cooling channel.",
  },
  four_seven_eight: {
    title: "4-7-8 Breath",
    sanskrit: "Pranava Rechaka",
    lines: [
      "Inhale quietly through the nose for 4 counts.",
      "Hold the breath for 7 counts.",
      "Exhale completely through the mouth for 8 counts — make a soft whoosh sound.",
      "The extended hold and exhale are what drive the parasympathetic shift.",
    ],
    tip: "The 7-count hold builds carbon dioxide tolerance and activates the Hering-Breuer reflex, which suppresses arousal. Do not rush the exhale.",
  },
  bhramari_sleep: {
    title: "Humming Breath",
    sanskrit: "Bhramari",
    lines: [
      "Close your eyes. Gently plug both ears with your index fingers.",
      "Inhale deeply through the nose.",
      "Exhale with a low, continuous hum from the throat.",
      "Feel the vibration resonate in your skull and chest.",
    ],
    tip: "The humming produces nitric oxide via nasal sinus resonance, which has a direct vasodilatory and anxiolytic effect. The vibration signals safety to the nervous system.",
  },
};
